import { useQueries } from '@tanstack/react-query'
import { api } from '../services'
import { queryKeys } from '../lib/queryClient'
import { SCHD_INTERVAL_MINUTES } from '../common/constants'
import { Viewport } from '../common/types'
import {
  ComparableSensorType,
  COMPARABLE_SENSOR_TYPES,
  SensorSubType,
  getComparableTypeByProduct,
  getSubTypeLabel,
  TEMPERATURE_PORT,
  BATTERY_PORT,
  POWER_PORT,
} from '../components/SensorTypeSelector'
import { SubMetric, metricToSubType, parsePortToken } from '../utils/portTokens'
import { CompareSelection, CompareVariant } from '../contexts/AppContext'
import { useAccountContext } from '../contexts/AccountContext'
import { getUserUnitSetting } from '../utils/unitSettings'
import { convertBaseToDisplay } from '../common/conversions'
import { compareFetchLimit } from '../utils/concurrencyLimit'

// Reading data point
export interface Reading {
  timestamp: number
  value: number
}

// Data for a single device's sensor
export interface DeviceSensorData {
  deviceId: string
  deviceName: string
  sensorPort: string
  depth: number | null
  readings: Reading[]
  hasData: boolean
  // Unit metadata — populated for every series so the chart can label axes,
  // group by unit (auto dual-axis / normalization), and show real tooltips.
  unitCategoryId: number | null
  sensorTypeKey: string          // e.g. 'battery', 'temperature'
  sensorTypeLabel: string        // e.g. 'Battery', 'Temperature', 'Rainfall · Rate'
  unit: string                   // abbreviation, e.g. 'V', '°C', 'cb' ('' if unknown)
  precision: number
  reverseYAxis: boolean          // true for Irrometer (higher = drier)
  // Which sub-metric of a rainfall/flow port this series is (null for plain
  // sensors). Several series can share one physical port, so consumers must
  // include this when keying a series.
  subType: SensorSubType | null
}

// Combined result for comparison chart
export interface MultiDeviceReadingsResult {
  deviceSensorData: DeviceSensorData[]
  isLoading: boolean
  isError: boolean
  errors: Error[]
}

const ONE_HOUR_SECONDS = 3600

/**
 * Turn raw base-unit readings for a rainfall/flow port into one of its
 * sub-metrics, in the user's display units.
 *
 * `formula` is the base->display conversion for *this* metric, chosen by the
 * caller (see deriveSubMetricUnitInfo) — the metric decides which unit category
 * it comes from, e.g. flow "total" uses the Volume formula table indexed by the
 * user's TotalVolume (acre-ft) unit.
 *
 * The math mirrors the single-device RainfallChart / FlowMeterChart so the same
 * sensor reads the same on both screens.
 */
export function processReadingsForSubType(
  readings: Reading[],
  subType: SensorSubType,
  formula: string
): Reading[] {
  // Sort readings by timestamp
  const sorted = [...readings].sort((a, b) => a.timestamp - b.timestamp)
  const convert = (v: number) => convertBaseToDisplay(v, formula)

  switch (subType) {
    // Rainfall amount per reading (base = meters)
    case 'rainfallVolume':
      return sorted.map(r => ({ timestamp: r.timestamp, value: convert(r.value) }))

    case 'rainfallRate': {
      // Sliding 1-hour window over the *base* readings, converted to the
      // rate category's base unit (mm/minute) before applying the user's
      // RainfallRate formula — same sequence as RainfallChart.
      const result: Reading[] = []
      let windowStart = 0
      let windowSum = 0

      for (let i = 0; i < sorted.length; i++) {
        const ts = sorted[i].timestamp
        windowSum += sorted[i].value
        // Drop readings that fell out of the trailing hour
        while (windowStart <= i && sorted[windowStart].timestamp <= ts - ONE_HOUR_SECONDS) {
          windowSum -= sorted[windowStart].value
          windowStart++
        }
        const millimetersPerHour = windowSum * 1000
        const millimetersPerMinute = millimetersPerHour / 60
        result.push({ timestamp: ts, value: convert(millimetersPerMinute) })
      }
      return result
    }

    case 'rainfallTotal': {
      // Running total of the converted per-reading amounts
      let total = 0
      return sorted.map(r => {
        total += convert(r.value)
        return { timestamp: r.timestamp, value: total }
      })
    }

    // Flow meter: volume and rate come from two separate series on the same
    // port, each already the right category — just convert.
    case 'flowVolume':
    case 'flowRate':
      return sorted.map(r => ({ timestamp: r.timestamp, value: convert(r.value) }))

    case 'flowTotal': {
      // Running total of per-reading volume in the TotalVolume unit
      let total = 0
      return sorted.map(r => {
        total += convert(r.value)
        return { timestamp: r.timestamp, value: total }
      })
    }

    default:
      return sorted
  }
}

/** Pull the reading list out of one /readings-fast entry (either shape). */
function extractReadings(sensorData: any): Reading[] {
  if (Array.isArray(sensorData?.data)) {
    return sensorData.data.map((r: any) => ({ timestamp: r.timestamp, value: r.value }))
  }
  if (sensorData?.readings && typeof sensorData.readings === 'object') {
    return Object.entries(sensorData.readings).map(([timestamp, value]) => ({
      timestamp: Number(timestamp),
      value: value as number,
    }))
  }
  return []
}

/** Unit category a sub-metric's raw readings must come from in /readings-fast. */
function sourceCategoryForSubType(subType: SensorSubType): number {
  switch (subType) {
    case 'flowRate': return 2   // VolumeRate
    case 'flowVolume':
    case 'flowTotal': return 1  // Volume
    default: return 14          // Rainfall (rate/total are derived from it)
  }
}

/**
 * Hook for parallel fetching of readings from multiple devices
 *
 * Uses React Query's useQueries for efficient parallel fetching with caching.
 *
 * @param deviceIds - Array of sigfoxIds to fetch readings for
 * @param sensorType - Type of sensor to fetch (e.g., 'humidity', 'soilMoistureVH400')
 * @param selectedSensors - Granular sensor selections (which ports from which devices)
 * @param viewport - Time range for fetching
 * @param deviceNames - Map of deviceId to device name for display
 * @param sensorConfigs - Map of deviceId to sensor configs (for depth info)
 * @param subType - Optional sub-type for sensors with multiple metrics (rainfall, flowMeter)
 * @param variant - 'single' (filter to one sensor type) or 'mixed' (any selected port, per-series units)
 */
export function useMultiDeviceReadings(
  deviceIds: string[],
  sensorType: ComparableSensorType | null,
  selectedSensors: CompareSelection[],
  viewport: Viewport,
  deviceNames: Record<string, string>,
  sensorConfigs: Record<string, Record<string, { sensorProductName: string; offset?: number }>>,
  subType?: SensorSubType | null,
  variant: CompareVariant = 'single'
): MultiDeviceReadingsResult {
  const { userUnitSettings, unitConversions } = useAccountContext()

  // Calculate time range and limit
  const from = Math.round(viewport.minimum / 1000) // Unix seconds
  const to = Math.round(viewport.maximum / 1000)
  const totalDays = (to - from) / (60 * 60 * 24)
  const numberOfDataPoints = totalDays * 24 * (60 / SCHD_INTERVAL_MINUTES)
  const limit = Math.round(numberOfDataPoints * 2)

  const isMixed = variant === 'mixed'

  // Get sensor type config for filtering (single mode only)
  const sensorTypeConfig = COMPARABLE_SENSOR_TYPES.find(t => t.key === sensorType)
  const productNames: readonly string[] = sensorTypeConfig?.productNames ?? []

  // Derive per-series unit metadata + value-conversion formula from a unit
  // category id. Mirrors GenericLineChart's unit derivation.
  const deriveUnitInfo = (
    unitCategoryId: number | null,
    productName: string
  ): { unit: string; precision: number; formula: string; reverseYAxis: boolean } => {
    const reverseYAxis = productName.toLowerCase().includes('irrometer')
    const category = unitCategoryId != null ? unitConversions[unitCategoryId as keyof typeof unitConversions] : undefined
    if (unitCategoryId == null || !category) {
      return { unit: '', precision: 2, formula: '@', reverseYAxis }
    }
    const { unitId, precision } = getUserUnitSetting(unitCategoryId, userUnitSettings, unitConversions)
    return {
      unit: category.abbreviations[unitId] ?? '',
      precision,
      formula: category.formulas[unitId] ?? '@',
      reverseYAxis,
    }
  }

  /**
   * Unit metadata for a rainfall/flow sub-metric.
   *
   * `displayCategoryId` is what the series reports as its unit category, which
   * is also what ComparisonChart groups Y-axes by — so metrics that are plotted
   * on different axes in the single-device charts (rain rate vs amount, flow
   * total vs volume) get distinct groups here too, while rain amount and rain
   * total deliberately share one.
   */
  const deriveSubMetricUnitInfo = (
    subType: SensorSubType
  ): { unit: string; precision: number; formula: string; displayCategoryId: number } => {
    // Category the *values* are converted with, and the category whose user
    // setting picks the unit within it. These differ only for flow total, which
    // is a Volume series shown in the user's TotalVolume unit (acre-ft).
    const valueCategoryId =
      subType === 'rainfallRate' ? 18 :        // RainfallRate (derived, mm/min base)
      subType === 'flowRate' ? 2 :             // VolumeRate
      subType.startsWith('flow') ? 1 :         // Volume (flow volume + total)
      14                                       // Rainfall (amount + total)
    const settingCategoryId = subType === 'flowTotal' ? 10 : valueCategoryId

    const category = unitConversions[valueCategoryId as keyof typeof unitConversions]
    const { unitId, precision } = getUserUnitSetting(settingCategoryId, userUnitSettings, unitConversions)

    return {
      unit: category?.abbreviations[unitId] ?? (subType === 'flowTotal' ? 'acre-ft' : ''),
      precision,
      formula: category?.formulas[unitId] ?? '@',
      // Rain amount + total share an axis (as in RainfallChart); rate, and flow
      // total, each get their own group.
      displayCategoryId: subType === 'flowTotal' ? 10 : valueCategoryId,
    }
  }

  // Create queries for each device. Single mode needs a sensor type; mixed mode
  // needs at least one explicitly selected sensor.
  // Battery and Port C power are served entirely by the separate
  // /battery-readings queries below, so single mode on either of them needs no
  // /readings-fast fan-out at all.
  const hasSelection = selectedSensors.some(s => s.sensorPorts.length > 0)
  const shouldFetch =
    deviceIds.length > 0 &&
    (isMixed
      ? hasSelection
      : !!sensorType && sensorType !== 'battery' && sensorType !== 'portCPower')
  const queryConfigs = shouldFetch
    ? deviceIds.map(deviceId => ({
        queryKey: queryKeys.compareReadings(deviceId, from, to),
        queryFn: async () => {
          const result = await compareFetchLimit(() => api.getReadings(deviceId, from, to, limit))
          return result.data || result
        },
        staleTime: 2 * 60 * 1000, // 2 minutes
        gcTime: 10 * 60 * 1000, // 10 minutes
        enabled: true,
      }))
    : []

  const queries = useQueries({
    queries: queryConfigs,
  })

  // Battery and Port C power are device-level series from a separate endpoint
  // (not in /readings-fast) — one request serves both. Fetch it only for
  // devices where the user explicitly picked one of them, in either mode, since
  // those tokens only land in a selection when the user picks them.
  // The cache entry is shared with the device view's useBatteryReadings, so it
  // holds the raw API response; the device id comes from the query's position.
  const batteryDeviceIds = deviceIds.filter(id => {
    const ports = selectedSensors.find(s => s.deviceId === id)?.sensorPorts ?? []
    return ports.includes(BATTERY_PORT) || ports.includes(POWER_PORT)
  })
  const batteryQueries = useQueries({
    queries: batteryDeviceIds.map(deviceId => ({
      queryKey: queryKeys.batteryReadings(deviceId, from, to),
      queryFn: () => compareFetchLimit(() => api.getBatteryReadings(deviceId, from, to)),
      staleTime: 5 * 60 * 1000, // 5 minutes - battery changes slowly
      gcTime: 15 * 60 * 1000,
      enabled: true,
    })),
  })

  // Determine overall loading and error state
  const isLoading = queries.some(q => q.isLoading) || batteryQueries.some(q => q.isLoading)
  const isError = queries.some(q => q.isError)
  const errors = queries.filter(q => q.isError).map(q => q.error as Error)

  // Process results into DeviceSensorData format
  const deviceSensorData: DeviceSensorData[] = []

  for (const [index, query] of queries.entries()) {
    const data = query.data
    const deviceId = deviceIds[index]

    // Skip if no data returned for this device
    if (!data || typeof data !== 'object') continue

    const deviceName = deviceNames[deviceId] || deviceId
    const deviceConfig = sensorConfigs[deviceId] || {}

    // Get selected ports for this device - only show explicitly selected sensors.
    // Selections are tokens: a bare port, or "port~metric" for a rainfall/flow
    // sub-metric in mixed mode. Group them by physical port so the readings
    // loop (which sees bare ports) can find them.
    const deviceSelection = selectedSensors.find(s => s.deviceId === deviceId)
    const metricsByPort = new Map<string, (SubMetric | null)[]>()
    for (const token of deviceSelection?.sensorPorts ?? []) {
      const { port, metric } = parsePortToken(token)
      metricsByPort.set(port, [...(metricsByPort.get(port) ?? []), metric])
    }

    // Track which series we've already added. Keyed by port *and* metric, since
    // one flow port yields two response keys and up to three series.
    const processedPorts = new Set<string>()

    // Find sensors that are explicitly selected. Single mode filters to the one
    // chosen sensor type; mixed mode accepts any comparable sensor, splitting
    // rainfall/flow ports into one series per selected sub-metric.
    for (const [sensorKey, sensorData] of Object.entries(data) as [string, any][]) {
      const [sensorPort, unitCategoryIdStr] = sensorKey.split('-')
      const unitCategoryId = unitCategoryIdStr ? Number(unitCategoryIdStr) : null
      const productName = sensorData?.metadata?.productName || sensorData?.productName || ''

      // Only show explicitly selected sensors - skip if not selected
      if (!metricsByPort.has(sensorPort)) continue

      // T0 is the device's board-temperature series; its product name isn't a
      // comparable sensor product, so map it to 'temperature' explicitly.
      const typeConfig = sensorPort === TEMPERATURE_PORT
        ? COMPARABLE_SENSOR_TYPES.find(t => t.key === 'temperature')
        : getComparableTypeByProduct(productName)

      // Mixed mode, sub-typed sensor (rainfall/flow): emit one series per
      // sub-metric the user selected on this port.
      if (isMixed && typeConfig && 'hasSubTypes' in typeConfig && typeConfig.hasSubTypes) {
        const rawReadings = extractReadings(sensorData)
        const portConfig = deviceConfig[sensorPort]

        for (const metric of metricsByPort.get(sensorPort) ?? []) {
          // A bare token on a sub-typed port (possible only from an old link)
          // names no metric, so there is nothing to plot.
          if (!metric) continue

          const metricSubType = metricToSubType(typeConfig.subTypeKey, metric)
          // Each metric reads from one of the port's response keys — skip the
          // others. A missing/unknown category is let through defensively.
          if (unitCategoryId !== null && unitCategoryId !== sourceCategoryForSubType(metricSubType)) continue

          const seriesKey = `${deviceId}-${sensorPort}~${metric}`
          if (processedPorts.has(seriesKey)) continue
          processedPorts.add(seriesKey)

          const info = deriveSubMetricUnitInfo(metricSubType)
          const readings = processReadingsForSubType(rawReadings, metricSubType, info.formula)

          deviceSensorData.push({
            deviceId,
            deviceName,
            sensorPort,
            depth: portConfig?.offset ?? null,
            readings,
            hasData: readings.length > 0,
            unitCategoryId: info.displayCategoryId,
            sensorTypeKey: typeConfig.key,
            sensorTypeLabel: `${typeConfig.label} · ${getSubTypeLabel(metricSubType)}`,
            unit: info.unit,
            precision: info.precision,
            reverseYAxis: false,
            subType: metricSubType,
          })
        }
        continue
      }

      if (isMixed) {
        // Mixed mode: any comparable type
        if (!typeConfig) continue
      } else {
        // Single mode: only the selected sensor type. Board temperature (T0) is
        // device-level — its product name isn't a comparable product, so match
        // it on the port instead.
        const isBoardTemp = sensorPort === TEMPERATURE_PORT && sensorType === 'temperature'
        if (!isBoardTemp && !productNames.includes(productName)) continue

        // For flow meter, filter by unit category based on sub-type
        // Volume = 1, VolumeRate = 2
        if (sensorType === 'flowMeter' && subType) {
          if (subType === 'flowRate' && unitCategoryId !== 2) continue
          if ((subType === 'flowVolume' || subType === 'flowTotal') && unitCategoryId !== 1) continue
        }

        // For rainfall, filter by unit category based on sub-type
        // Rainfall = 14. We use it for all sub-types and calculate rate/total from it.
        if (sensorType === 'rainfall' && subType && unitCategoryId !== null) {
          if (unitCategoryId !== 14) continue
        }
      }

      // Skip if we've already processed this port (for flow meter, multiple readings per port)
      const portKey = `${deviceId}-${sensorPort}`
      if (processedPorts.has(portKey)) continue
      processedPorts.add(portKey)

      let readings = extractReadings(sensorData)

      // Single-mode sub-typed series (rainfall/flow) carry their own per-metric
      // conversion; everything else uses the port's unit category.
      const hasSubType = !isMixed && !!subType
      const subMetricInfo = hasSubType ? deriveSubMetricUnitInfo(subType as SensorSubType) : null
      const unitInfo = deriveUnitInfo(unitCategoryId, productName)

      if (subMetricInfo) {
        readings = processReadingsForSubType(readings, subType as SensorSubType, subMetricInfo.formula)
      } else if (unitInfo.formula !== '@' && readings.length > 0) {
        readings = readings.map(r => ({
          timestamp: r.timestamp,
          value: convertBaseToDisplay(r.value, unitInfo.formula),
        }))
      }

      // Get depth from config
      const portConfig = deviceConfig[sensorPort]
      const depth = portConfig?.offset ?? null

      deviceSensorData.push({
        deviceId,
        deviceName,
        sensorPort,
        depth,
        readings,
        hasData: readings.length > 0,
        unitCategoryId: subMetricInfo?.displayCategoryId ?? unitCategoryId,
        sensorTypeKey: typeConfig?.key ?? sensorType ?? '',
        sensorTypeLabel: typeConfig?.label ?? sensorTypeConfig?.label ?? '',
        unit: subMetricInfo?.unit ?? unitInfo.unit,
        precision: subMetricInfo?.precision ?? unitInfo.precision,
        reverseYAxis: unitInfo.reverseYAxis,
        subType: hasSubType ? (subType as SensorSubType) : null,
      })
    }
  }

  // Append the device-level series carried by /battery-readings, which returns
  // { battery: {ts: volts}, power: {ts: volts} } — both already in base volts.
  const batteryUnit = deriveUnitInfo(8, 'Battery') // 8 = ElectricPotentialDifference
  const batterySeriesSpecs = [
    { port: BATTERY_PORT, mapKey: 'battery', typeKey: 'battery', label: 'Battery', flatFallback: true },
    { port: POWER_PORT, mapKey: 'power', typeKey: 'portCPower', label: 'Port C Power', flatFallback: false },
  ] as const

  for (const [index, query] of batteryQueries.entries()) {
    const deviceId = batteryDeviceIds[index]
    const data = query.data?.data ?? query.data
    if (!data || typeof data !== 'object') continue

    const selectedPorts = selectedSensors.find(s => s.deviceId === deviceId)?.sensorPorts ?? []

    for (const spec of batterySeriesSpecs) {
      if (!selectedPorts.includes(spec.port)) continue

      const namedMap = (data as any)[spec.mapKey]
      // Battery falls back to a flat map in case the endpoint ever returns one
      // directly; a flat map is battery voltage, never the power rail.
      const valueMap = namedMap && typeof namedMap === 'object'
        ? namedMap
        : spec.flatFallback ? data : null
      if (!valueMap) continue

      let readings: Reading[] = Object.entries(valueMap)
        .map(([timestamp, value]) => ({ timestamp: Number(timestamp), value: Number(value) }))
        .filter(r => Number.isFinite(r.timestamp) && Number.isFinite(r.value))
        .sort((a, b) => a.timestamp - b.timestamp)

      if (batteryUnit.formula !== '@' && readings.length > 0) {
        readings = readings.map(r => ({ timestamp: r.timestamp, value: convertBaseToDisplay(r.value, batteryUnit.formula) }))
      }

      deviceSensorData.push({
        deviceId,
        deviceName: deviceNames[deviceId] || deviceId,
        sensorPort: spec.port,
        depth: null,
        readings,
        hasData: readings.length > 0,
        unitCategoryId: 8,
        sensorTypeKey: spec.typeKey,
        sensorTypeLabel: spec.label,
        unit: batteryUnit.unit || 'V',
        precision: batteryUnit.precision,
        reverseYAxis: false,
        subType: null,
      })
    }
  }

  return {
    deviceSensorData,
    isLoading,
    isError,
    errors,
  }
}

/**
 * Hook for fetching sensor configs for multiple devices
 * Used to build the DeviceSensorInfo for SensorTypeSelector
 */
export function useMultiDeviceSensorConfigs(
  deviceIds: string[],
  timestamp: number
) {
  // Only create queries when we have device IDs
  const queryConfigs = deviceIds.length > 0
    ? deviceIds.map(deviceId => ({
        queryKey: ['sensor-configs', deviceId, timestamp],
        queryFn: async () => {
          const result = await api.getSensorConfigs(deviceId, timestamp)
          return {
            deviceId,
            configs: result.data || result,
          }
        },
        staleTime: 5 * 60 * 1000, // 5 minutes
        gcTime: 15 * 60 * 1000, // 15 minutes
        enabled: true,
      }))
    : []

  const queries = useQueries({
    queries: queryConfigs,
  })

  const isLoading = queries.some(q => q.isLoading)
  const isError = queries.some(q => q.isError)

  // Build map of deviceId -> sensor configs
  const sensorConfigs: Record<string, Record<string, { sensorProductName: string; offset?: number }>> = {}

  for (const query of queries) {
    if (query.data) {
      sensorConfigs[query.data.deviceId] = query.data.configs
    }
  }

  return {
    sensorConfigs,
    isLoading,
    isError,
  }
}
